import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Flame } from 'lucide-react';
const CHECKOUT_URL = 'https://pay.hotmart.com/G105750225G?off=exnypaed';
const COUNTDOWN_MINUTES = 60;
const STORAGE_START_KEY = 'shadow_offer_started_at';
const STORAGE_DISMISSED_KEY = 'shadow_banner_dismissed';
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
function formatTime(totalSeconds: number) {
  const safe = Math.max(0, totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor(safe % 3600 / 60);
  const seconds = safe % 60;
  return {
    hours,
    minutes,
    seconds
  };
}
export function UrgencyBanner() {
  const [isDismissed, setIsDismissed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_MINUTES * 60);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(STORAGE_DISMISSED_KEY) === '1') {
      setIsDismissed(true);
      setMounted(true);
      return;
    }
    let startedAt = Number(sessionStorage.getItem(STORAGE_START_KEY));
    if (!startedAt || Number.isNaN(startedAt)) {
      startedAt = Date.now();
      sessionStorage.setItem(STORAGE_START_KEY, String(startedAt));
    }
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const remaining = COUNTDOWN_MINUTES * 60 - elapsed;
      setSecondsLeft(Math.max(0, remaining));
    };
    tick();
    setMounted(true);
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, []);
  const dismiss = () => {
    setIsDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_DISMISSED_KEY, '1');
    } catch {}
  };
  const {
    hours,
    minutes,
    seconds
  } = formatTime(secondsLeft);
  const isExpired = secondsLeft === 0;
  const isCritical = secondsLeft > 0 && secondsLeft <= 5 * 60;
  const showBanner = mounted && !isDismissed;
  return <AnimatePresence data-id="element-272">
      {showBanner && <motion.div initial={{
      y: -60,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: -60,
      opacity: 0
    }} transition={{
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }} className="sticky top-0 z-[80] w-full bg-blood text-white border-b border-black/20 shadow-[0_4px_20px_rgba(184,20,28,0.35)]" role="region" aria-label="Oferta por tiempo limitado" data-id="element-273">
          {/* Subtle noise overlay */}
          <div className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }} data-id="element-274" />

          <div className="relative max-w-7xl mx-auto px-3 md:px-6 py-2 md:py-2.5 flex items-center justify-between gap-2 md:gap-4" data-id="element-275">
            {/* Left: badge + offer message */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1" data-id="element-276">
              <motion.div animate={{
            rotate: [0, -8, 8, 0]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="flex-shrink-0 hidden sm:flex items-center justify-center w-7 h-7 rounded-full bg-white/15" aria-hidden="true" data-id="element-277">
                <Flame size={14} strokeWidth={2.5} data-id="element-278" />
              </motion.div>

              <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-3" data-id="element-279">
                <span className="font-sans text-[10px] md:text-xs font-bold uppercase tracking-widest text-white/85 leading-tight" data-id="element-280">
                  Oferta de hoy
                </span>
                <span className="font-sans text-xs md:text-sm font-medium leading-tight truncate" data-id="element-281">
                  Solamente por <strong className="font-bold" data-id="element-282">$14.99</strong>{' '}
                  <span className="text-white/70 line-through" data-id="element-283">$27</span>
                  <span className="hidden md:inline text-white/85" data-id="element-284">
                    {' '}
                    — vuelve a $27 cuando termine
                  </span>
                </span>
              </div>
            </div>

            {/* Center: countdown */}
            <div className="flex-shrink-0 flex items-center gap-1.5 md:gap-2" data-id="element-285">
              <span className="hidden md:inline font-sans text-[10px] uppercase tracking-widest text-white/70" data-id="element-286">
                {isExpired ? '¡Cerrando!' : 'Termina en'}
              </span>
              <motion.div animate={isCritical ? {
            opacity: [1, 0.5, 1]
          } : {
            opacity: 1
          }} transition={{
            duration: 0.9,
            repeat: isCritical ? Infinity : 0,
            ease: 'easeInOut'
          }} className="flex items-center gap-0.5 md:gap-1 font-mono font-bold text-sm md:text-base tabular-nums bg-black/30 border border-white/15 px-2 md:px-2.5 py-1 rounded-[2px]" aria-live="polite" aria-atomic="true" data-id="element-287">
                <span className="min-w-[18px] text-center" data-id="element-288">{pad(hours)}</span>
                <span className="text-white/50" data-id="element-289">:</span>
                <span className="min-w-[18px] text-center" data-id="element-290">{pad(minutes)}</span>
                <span className="text-white/50" data-id="element-291">:</span>
                <span className="min-w-[18px] text-center" data-id="element-292">{pad(seconds)}</span>
              </motion.div>
            </div>

            {/* Right: CTA + dismiss */}
            <div className="flex-shrink-0 flex items-center gap-1 md:gap-2" data-id="element-293">
              <motion.a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.96
          }} className="hidden sm:inline-flex items-center gap-2 bg-white text-blood px-3 md:px-4 py-1.5 font-sans text-[11px] md:text-xs font-bold uppercase tracking-widest no-underline hover:bg-foreground transition-colors rounded-[2px]" data-id="element-294">
                Comprar
                <ArrowRight size={12} strokeWidth={3} data-id="element-295" />
              </motion.a>

              {/* Mobile-only icon CTA */}
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" aria-label="Comprar ahora" className="sm:hidden inline-flex items-center justify-center w-8 h-8 bg-white text-blood rounded-[2px]" data-id="element-296">
                <ArrowRight size={14} strokeWidth={3} data-id="element-297" />
              </a>

              <button onClick={dismiss} aria-label="Cerrar oferta" className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors rounded-[2px]" data-id="element-298">
                <X size={14} strokeWidth={2.5} data-id="element-299" />
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}